import { IMAGE_HOSTS, IMAGE_HOST_PATTERNS, STORAGE_PROXY_BUCKETS } from './image-host-allowlist'

/**
 * FRIDGE-1506: whether next/image may optimize this source — a same-origin
 * path with no query string (next.config's localPatterns accept every local
 * path with `search: ''`), a path in one of the Storage buckets with any query
 * string (FRIDGE-1547: localPatterns accept `/<bucket>/**` with any search, for
 * the `?v=` cache-busters; the bucket list is shared with next.config), or an
 * https host next.config's remotePatterns allow (lib/image-host-allowlist.js,
 * the list it is built from). A source the optimizer would refuse stays a
 * plain <img>, so switching a surface to next/image can never break a photo it
 * shows today.
 */
export function canOptimizeImageSrc(src: string | null | undefined): boolean {
  if (!src) return false
  if (src.startsWith('/') && !src.startsWith('//')) {
    if (!src.includes('?')) return true
    return (STORAGE_PROXY_BUCKETS as readonly string[]).some((bucket) => src.startsWith(`/${bucket}/`))
  }
  let host: string
  try {
    const url = new URL(src)
    if (url.protocol !== 'https:') return false
    host = url.hostname
  } catch {
    return false
  }
  if ((IMAGE_HOSTS as readonly string[]).includes(host)) return true
  return (IMAGE_HOST_PATTERNS as readonly string[]).some((pattern) => pattern.startsWith('**.') && host.endsWith(pattern.slice(2)))
}

/** Widths the image optimizer serves (next.config.js images.imageSizes and deviceSizes). */
const OPTIMIZER_WIDTHS = [16, 32, 48, 64, 96, 128, 256, 384, 640, 750, 828, 1080, 1200, 1920, 2048] as const

/**
 * FRIDGE-1547: a src and srcset through the image optimizer for a surface that
 * renders a plain <img> on purpose (a lazy logo React would otherwise preload,
 * FRIDGE-311), so the browser takes a file sized to the slot instead of the
 * stored original. A source the optimizer refuses comes back as it is.
 */
export function optimizedImgSrc(src: string, widths: readonly (typeof OPTIMIZER_WIDTHS)[number][], quality = 75): { src: string; srcSet?: string } {
  if (!canOptimizeImageSrc(src) || widths.length === 0) return { src }
  const url = (w: number) => `/_next/image?url=${encodeURIComponent(src)}&w=${w}&q=${quality}`
  return { src: url(widths[widths.length - 1]), srcSet: widths.map((w) => `${url(w)} ${w}w`).join(', ') }
}
